import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { OfferResourcesCarousel, type OfferResource } from './OfferResourcesCarousel';
export type LibraryResource = {
  title: string;
  price: number;
  description: string;
  image: string;
};
type PurchaseType = 'her' | 'her_biblioteca';
type OfferProductStepProps = {
  email: string;
  isValid: boolean;
  submitting: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  resources: OfferResource[];
  library: LibraryResource[];
  purchaseType: PurchaseType;
  onEmailChange: (email: string) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onSelectPurchase: (type: PurchaseType) => void;
};
function Chevron({
  open
}: {
  open: boolean;
}) {
  return <motion.svg width="10" height="6" viewBox="0 0 10 6" fill="none" animate={{
    rotate: open ? 180 : 0
  }} transition={{
    duration: 0.2,
    ease: [0.23, 1, 0.32, 1]
  }} aria-hidden="true" data-id="element-477" data-mp="3i6rlfdvvuwq0">
      <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" data-id="element-478" data-mp="2zf2pu9hho6um" />
    </motion.svg>;
}
function HERBrandMark() {
  const shouldReduceMotion = useReducedMotion();
  return <div className="flex justify-center pt-0.5 pb-0.5" aria-label="H.E.R." data-id="element-479" data-mp="395f97ydkhtz6">
      <svg viewBox="0 0 360 64" className="h-auto w-[92%] max-w-[360px] overflow-visible" fill="none" role="img" aria-label="H.E.R." data-id="element-480" data-mp="345svzoxqodqd">
        {/* Línea izquierda */}
        <motion.path d="M8 32 H112" stroke="#C8A870" strokeWidth="1.2" strokeLinecap="round" initial={{
        pathLength: shouldReduceMotion ? 1 : 0,
        opacity: shouldReduceMotion ? 0.75 : 0
      }} whileInView={{
        pathLength: 1,
        opacity: 0.75
      }} viewport={{
        once: true,
        amount: 0.7
      }} transition={{
        pathLength: {
          duration: shouldReduceMotion ? 0 : 0.7,
          ease: [0.22, 1, 0.36, 1]
        },
        opacity: {
          duration: shouldReduceMotion ? 0 : 0.3
        }
      }} data-id="element-481" data-mp="2ggqd6drdceua" />

        {/* Línea derecha */}
        <motion.path d="M248 32 H352" stroke="#C8A870" strokeWidth="1.2" strokeLinecap="round" initial={{
        pathLength: shouldReduceMotion ? 1 : 0,
        opacity: shouldReduceMotion ? 0.75 : 0
      }} whileInView={{
        pathLength: 1,
        opacity: 0.75
      }} viewport={{
        once: true,
        amount: 0.7
      }} transition={{
        pathLength: {
          duration: shouldReduceMotion ? 0 : 0.7,
          ease: [0.22, 1, 0.36, 1],
          delay: shouldReduceMotion ? 0 : 0.15
        },
        opacity: {
          duration: shouldReduceMotion ? 0 : 0.3,
          delay: shouldReduceMotion ? 0 : 0.15
        }
      }} data-id="element-482" data-mp="7rqyw0hn8676" />

        {/* Logo central */}
        <motion.text x="180" y="40" textAnchor="middle" fill="#C8A870" className="font-display" style={{
        fontSize: '30px',
        fontWeight: 500,
        letterSpacing: '0.18em'
      }} initial={{
        opacity: shouldReduceMotion ? 1 : 0,
        y: shouldReduceMotion ? 0 : 5
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.7
      }} transition={{
        duration: shouldReduceMotion ? 0 : 0.7,
        delay: shouldReduceMotion ? 0 : 0.25,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-483" data-mp="3rqiiaazhkxh">
          H · E · R
        </motion.text>
      </svg>
    </div>;
}
export function OfferProductStep({
  email,
  isValid,
  submitting,
  inputRef,
  resources,
  library,
  purchaseType,
  onEmailChange,
  onSubmit,
  onSelectPurchase
}: OfferProductStepProps) {
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [expandedLibrary, setExpandedLibrary] = useState<number | null>(null);
  const isLibrarySelected = purchaseType === 'her_biblioteca';
  const libraryTotal = library.reduce((sum, item) => sum + item.price, 0);
  const libraryCover = library[0];
  const librarySavings = libraryTotal - 8000;
  const toggleLibrary = () => {
    onSelectPurchase(isLibrarySelected ? 'her' : 'her_biblioteca');
  };
  return <motion.div key="product" initial={{
    opacity: 0,
    x: 16
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -16
  }} className="space-y-6" data-id="element-484" data-mp="2hzi1cqeq2kdr">
      {/* Encabezado */}
      <div className="space-y-2 text-center" data-id="element-485" data-mp="287vsv1trqzep">
        <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#C8A870]" data-id="element-486" data-mp="286t00ocvymw5">
          Tu acceso H.E.R.
        </p>

        <p className="font-display text-[30px] leading-tight text-[#F0E8D8] md:text-[34px]" data-id="element-487" data-mp="1z7pihotdafjr">
          Protocolo H.E.R. · 21 días
        </p>

        <p className="mx-auto max-w-[280px] font-body text-[14px] leading-relaxed text-[#F0E8D8]/55" data-id="element-488" data-mp="248d949dooudg">
          Un programa simple de 21 días para ordenar tu comida, tu movimiento y
          tu descanso.
        </p>
      </div>

      {/* Carrusel de recursos */}
      <OfferResourcesCarousel items={resources} data-id="element-489" data-mp="3jovjzu4dr875" />

      {/* Firma de marca */}
      <HERBrandMark data-id="element-490" data-mp="3onf7w02jau25" />

      {/* Precio principal */}
      <div className="rounded-[10px] border border-[#C8A870]/25 bg-[#C8A870]/[0.04] px-5 py-4 text-center" data-id="element-491" data-mp="3om41rtlm9a7v">
        <div className="flex flex-wrap items-baseline justify-center gap-x-3 gap-y-1" data-id="element-492" data-mp="1cxh5z02sokii">
          <p className="whitespace-nowrap font-display text-[13px] leading-none text-[#F0E8D8]/40" data-id="element-493" data-mp="182a5e14tnvxt">
            <span className="uppercase tracking-[0.16em]" data-id="element-494" data-mp="1mqo9efdz5qfy">
              Antes
            </span>{' '}
            <span className="line-through" data-id="element-495" data-mp="1hwjxdhpuqs80">$44.000</span>
          </p>

          <p className="whitespace-nowrap font-display text-[39px] leading-none text-gradient-gold" data-id="element-496" data-mp="1voqcr1l6kv6e">
            $12.000{' '}
            <span className="font-body text-[16px] text-[#C8A870]/70" data-id="element-497" data-mp="1rqixycaa23xu">
              ARS
            </span>
          </p>
        </div>

        <p className="mt-2 font-body text-[13px] leading-relaxed text-[#C8A870]" data-id="element-498" data-mp="20pe6hv0snq4b">
          Pago único o 3 cuotas de $4.000 con Mercado Pago
        </p>
      </div>

      {/* Biblioteca complementaria */}
      <div className="text-center" data-id="element-499" data-mp="6bmvos6qb07a">
        <p className="font-display text-[17px] leading-tight text-[#F0E8D8]/78" data-id="element-500" data-mp="6co8b13e7ur4">
          ¿Querés ir un paso más allá?
        </p>

        <p className="mt-1 font-body text-[13px] leading-relaxed text-[#F0E8D8]/45" style={{
        color: "#C8A870"
      }} data-id="element-501" data-mp="26fphe9j4wd53">
          Sumá la Biblioteca Complementaria y profundizá tu experiencia H.E.R.
        </p>

        <button type="button" onClick={toggleLibrary} aria-pressed={isLibrarySelected} className={`mt-4 flex w-full items-center gap-3 rounded-[10px] border px-3 py-3 text-left transition-colors duration-200 ease-out focus:outline-none focus-visible:ring-1 focus-visible:ring-[#C8A870] ${isLibrarySelected ? 'border-[#C8A870]/55 bg-[#C8A870]/10' : 'border-[#C8A870]/22 bg-[#C8A870]/[0.02] hover:bg-[#C8A870]/[0.05]'}`} data-id="element-502" data-mp="2kcvze1w1a7t9">
          {libraryCover && <div className="h-[64px] w-[48px] shrink-0 overflow-hidden rounded-[4px] border border-[#C8A870]/25 bg-[#090807]" data-id="element-503" data-mp="332c0orfjm4aq">
              <img src={libraryCover.image} alt={libraryCover.title} loading="lazy" decoding="async" className="h-full w-full object-cover" data-id="element-504" data-mp="380lw9356j6wv" />
            </div>}

          <div className="min-w-0 flex-1" data-id="element-505" data-mp="3n6rkf4ehynl2">
            <p className="font-display text-[16px] leading-tight text-[#F0E8D8]" data-id="element-506" data-mp="3n3ditydnqieo">
              Biblioteca Complementaria
            </p>

            <p className="mt-0.5 font-body text-[12px] leading-snug text-[#F0E8D8]/50" style={{
            color: "#8F7952"
          }} data-id="element-507" data-mp="3d6sd1attd39s">
              Código Hormonal + Análisis de Sangre + Suplementación
            </p>

            {isLibrarySelected && <p className="mt-1 font-body text-[12px] font-semibold text-[#C8A870]" data-id="element-508" data-mp="9q20se7n1tzn">
                Total H.E.R. + Biblioteca: $20.000
              </p>}
          </div>

          <div className="flex shrink-0 flex-col items-end gap-1.5" data-id="element-509" data-mp="3w5d452vpg8nf">
            <span aria-hidden="true" className={`flex h-5 w-5 items-center justify-center rounded-[4px] border text-[12px] leading-none ${isLibrarySelected ? 'border-[#C8A870] bg-[#C8A870] text-[#0a0807]' : 'border-[#C8A870]/40 text-transparent'}`} data-id="element-510" data-mp="3s52yxsfqlm9m">
              ✓
            </span>

            <span className="whitespace-nowrap font-display text-[13px] text-[#C8A870]" data-id="element-511" data-mp="17en80j2u8z2c">
              {isLibrarySelected ? 'Biblioteca agregada' : 'Agregar · $8.000'}
            </span>
          </div>
        </button>

        {/* Detalle de recursos */}
        <button type="button" onClick={() => setIsDetailOpen(open => !open)} aria-expanded={isDetailOpen} className="mt-3 inline-flex items-center gap-1.5 font-body text-[11px] uppercase tracking-[0.14em] text-[#F0E8D8]/45 transition-colors duration-200 hover:text-[#C8A870] focus:outline-none focus-visible:ring-1 focus-visible:ring-[#C8A870]" style={{
        color: "#C8A870"
      }} data-id="element-512" data-mp="xi0mrt6lo5zs">
          {isDetailOpen ? 'Ocultar detalle' : 'Ver el detalle de los 3 recursos'}

          <span className="text-[#C8A870]/70" data-id="element-513" data-mp="1l58roa049d1a">
            <Chevron open={isDetailOpen} data-id="element-514" data-mp="1l84otw453xoo" />
          </span>
        </button>

        <AnimatePresence initial={false} data-id="element-515" data-mp="4q8lm2z0wwfq">
          {isDetailOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          height: {
            duration: 0.28,
            ease: [0.23, 1, 0.32, 1]
          },
          opacity: {
            duration: 0.18,
            ease: 'easeOut'
          }
        }} className="overflow-hidden text-left" data-id="element-516" data-mp="9refi44f6pqu">
              <div className="mt-4 rounded-[10px] border border-[#C8A870]/18 bg-[#C8A870]/[0.02] px-4 py-3" data-id="element-517" data-mp="2svwciedtaohz">
                <div className="border-t border-[#C8A870]/10" data-id="element-518" data-mp="2091urp8dq387">
                  {library.map((item, index) => {
                const isExpanded = expandedLibrary === index;
                return <div key={item.title} className="border-b border-[#C8A870]/10" data-id="element-519" data-mp="2e2kqn3i5ydro" data-mpi={index}>
                        <button type="button" onClick={() => setExpandedLibrary(isExpanded ? null : index)} aria-expanded={isExpanded} className="flex w-full items-center gap-3 py-2.5 text-left focus:outline-none" data-id="element-520" data-mp="298gjaj4zk3q2">
                          <div className="h-[48px] w-[36px] shrink-0 overflow-hidden rounded-[3px] border border-[#C8A870]/20 bg-[#090807]" data-id="element-521" data-mp="1hgr5wzak85t3">
                            <img src={item.image} alt={item.title} loading="lazy" decoding="async" className="h-full w-full object-cover" data-id="element-522" data-mp="1wp51kiy6hviy" />
                          </div>

                          <span className="flex-1 font-display text-[15px] leading-tight text-[#F0E8D8]" data-id="element-523" data-mp="1noow53xvkebg">
                            {item.title}
                          </span>

                          <span className="shrink-0 font-display text-[14px] text-[#C8A870]" data-id="element-524" data-mp="1irgf56ogqgbs">
                            ${item.price.toLocaleString('es-AR')}
                          </span>

                          <span className="shrink-0 text-[#C8A870]" data-id="element-525" data-mp="18xpuydzdcs1w">
                            <Chevron open={isExpanded} data-id="element-526" data-mp="14ziab81sb6lf" />
                          </span>
                        </button>

                        <AnimatePresence initial={false} data-id="element-527" data-mp="zyclvmf5q5hp">
                          {isExpanded && <motion.div initial={{
                      opacity: 0,
                      height: 0
                    }} animate={{
                      opacity: 1,
                      height: 'auto'
                    }} exit={{
                      opacity: 0,
                      height: 0
                    }} transition={{
                      duration: 0.2
                    }} className="overflow-hidden" data-id="element-528" data-mp="v1cacvzjgaj7">
                              <p className="pb-3 pl-[48px] pr-2 font-body text-[13px] leading-relaxed text-[#F0E8D8]/55" data-id="element-529" data-mp="2nwveuasm6zh4">
                                {item.description}
                              </p>
                            </motion.div>}
                        </AnimatePresence>
                      </div>;
              })}
                </div>

                <p className="pt-2 text-center font-body text-[12px] text-[#F0E8D8]/40" data-id="element-530" data-mp="31p39nx0c8gd6">
                  Valor individual $
                  {libraryTotal.toLocaleString('es-AR')} · sumando los 3
                  ahorrás ${librarySavings.toLocaleString('es-AR')}
                </p>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Email y CTA */}
      <form onSubmit={onSubmit} className="space-y-3 text-left" data-id="element-531" data-mp="37is5gxyl3drb">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#F0E8D8]/45" style={{
        fontSize: '12px',
        color: "#EFE7D7"
      }} data-id="element-532" data-mp="3m8g6dy2usaei">
          ¿Dónde querés recibir tu acceso?
        </p>

        <div className="relative" data-id="element-533" data-mp="2tjinbsqti9kp">
          <input ref={inputRef} type="email" name="email" autoComplete="email" inputMode="email" value={email} onChange={event => onEmailChange(event.target.value)} placeholder="Tu email" className="w-full bg-transparent py-3 font-body text-[16px] text-[#F0E8D8] placeholder-[#F0E8D8]/25 focus:outline-none" style={{
          borderBottom: `1px solid ${isValid ? 'rgba(200,168,112,0.6)' : 'rgba(240,232,216,0.15)'}`
        }} data-id="element-534" data-mp="2tktrvus37lcv" />

          {isValid && <span className="absolute right-0 top-3 text-[#C8A870]" data-id="element-535" data-mp="dswgn91e5a47">
              ✓
            </span>}
        </div>

        <button type="submit" disabled={!isValid || submitting} className="w-full rounded-[3px] py-4 font-sans text-[13px] font-bold uppercase tracking-[0.12em] transition-opacity duration-200 disabled:cursor-not-allowed" style={{
        background: isValid ? '#C8A870' : 'rgba(200,168,112,0.2)',
        color: isValid ? '#0a0807' : 'rgba(200,168,112,0.4)',
        opacity: submitting ? 0.7 : 1
      }} data-id="element-536" data-mp="lcozdulheovo">
          {submitting ? 'Un momento…' : 'Continuar →'}
        </button>
      </form>
    </motion.div>;
}